// Reports (V41-V45, X03-X04, X17-X20, E11). Everything renders live from the engine; export gives xlsx, print gives PDF.
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { FileDown, FlaskConical, Printer } from "lucide-react";
import { BD_LABEL, useProject } from "@/hooks/project";
import { Badge, Button, Card, Checkbox, Field, PageHeader, Select, Tabs, cn, fmtDate, fmtIdx, fmtNum, fmtPct, useToast } from "@/components/ui";
import { ExportButton } from "@/components/Importer";
import { BarChart, COLORS, LineChart, type SeriesDef } from "@/components/charts";
import { PeriodGrid, type GridColumn, type GridRow } from "@/components/periodGrid";
import { compareKeys, group, metricsAt, deliverableStatus, type Agg, type Metrics, type CodeFilter } from "@/engine/compute";
import type { Breakdown, Period, Settings, Unit } from "@/engine/types";
import type { Cell, OutSheet } from "@/lib/xlsx";
import { xlDate } from "@/lib/xlsx";
import { toDay } from "@/engine/dates";
import { buildCobra, COBRA_COLUMNS, type CobraRow, type EacBasis } from "@/lib/cobra";
import { writeCobraWorkbook } from "@/lib/cobraXlsx";
import { download } from "@/lib/xlsx";

export type ReportKey = "summary" | "curve" | "curve-combined" | "histogram" | "productivity" | "worklist" | "milestones";
const TITLES: Record<ReportKey, string> = {
  summary: "Progress summary",
  curve: "Progress curve",
  "curve-combined": "Progress curve (combined)",
  productivity: "Productivity",
  histogram: "Histogram",
  worklist: "Work items list",
  milestones: "Milestone report",
};

interface Opts {
  by: Breakdown[];
  filter: CodeFilter;
  asOf: number;
  unit: Unit;
  cost: boolean;
}

function Toolbar({ o, setO, allowGroup = true, extra }: { o: Opts; setO: (o: Opts) => void; allowGroup?: boolean; extra?: ReactNode }) {
  const { activeBreakdowns, nodes, periods, currentIdx, project, b } = useProject();
  const hasUsd = b.deliverables.some((d) => d.unit === "$");
  return (
    <div className="no-print mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-surface p-3">
      {allowGroup && (
        <Field label="Group by (in order)">
          <div className="flex gap-1">
            {[0, 1, 2].map((i) => (
              <Select
                key={i}
                aria-label={`Group level ${i + 1}`}
                value={o.by[i] ?? ""}
                className="w-28"
                disabled={i > o.by.length}
                onChange={(e) => {
                  const by = [...o.by.slice(0, i), ...(e.target.value ? [e.target.value as Breakdown] : [])];
                  setO({ ...o, by });
                }}
              >
                <option value="">{i === 0 ? "Total only" : "–"}</option>
                {activeBreakdowns
                  .filter((t) => !o.by.slice(0, i).includes(t))
                  .map((t) => (
                    <option key={t} value={t}>
                      {BD_LABEL[t]}
                    </option>
                  ))}
              </Select>
            ))}
          </div>
        </Field>
      )}
      {activeBreakdowns.map((t) =>
        nodes[t].length ? (
          <Field key={t} label={`${BD_LABEL[t]} filter`}>
            <Select aria-label={`${BD_LABEL[t]} filter`} className="w-28" value={o.filter[t]?.[0] ?? ""} onChange={(e) => setO({ ...o, filter: { ...o.filter, [t]: e.target.value ? [e.target.value] : [] } })}>
              <option value="">All</option>
              {nodes[t].map((n) => (
                <option key={n.code} value={n.code}>
                  {n.code}
                </option>
              ))}
            </Select>
          </Field>
        ) : null,
      )}
      <Field label="As of cutoff">
        <Select aria-label="As of" className="w-44" value={o.asOf} onChange={(e) => setO({ ...o, asOf: Number(e.target.value) })}>
          {periods.map((p, i) => (
            <option key={p.number} value={i}>
              P{p.number} · {fmtDate(p.cutoff)}
              {i === currentIdx ? " (current)" : p.closed ? " (closed)" : ""}
            </option>
          ))}
        </Select>
      </Field>
      {hasUsd && (
        <Field label="Unit">
          <Select className="w-24" value={o.unit} onChange={(e) => setO({ ...o, unit: e.target.value as Unit })}>
            <option value="h">hours</option>
            <option value="$">$</option>
          </Select>
        </Field>
      )}
      {o.unit === "h" && project.settings.laborRate > 0 && <Checkbox label={`Cost view (× $${project.settings.laborRate}/h)`} checked={o.cost} onChange={(v) => setO({ ...o, cost: v })} />}
      {extra}
    </div>
  );
}

export function ReportsPage({ report }: { report: ReportKey }) {
  const { currentIdx, periods, project } = useProject();
  const [o, setO] = useState<Opts>({ by: ["dbs"], filter: {}, asOf: Math.max(0, currentIdx), unit: "h", cost: false });
  if (!periods.length) return <PageHeader title={TITLES[report]} sub="Set up the calendar and deliverables first." />;
  const settingsNote = project.settings.includeIndirects ? <Badge tone="violet">indirects included in EVM</Badge> : null;
  return (
    <div>
      <PageHeader
        title={TITLES[report]}
        sub={
          <>
            {project.code} · {project.name} · as of {fmtDate(periods[o.asOf]?.cutoff)} {settingsNote}
          </>
        }
        actions={
          <Button onClick={() => window.print()}>
            <Printer className="size-4" /> Print / PDF
          </Button>
        }
      />
      {report === "summary" && <Summary o={o} setO={setO} />}
      {report === "curve" && <Curve o={o} setO={setO} />}
      {report === "curve-combined" && <CurveCombined o={o} setO={setO} />}
      {report === "histogram" && <Histogram o={o} setO={setO} />}
      {report === "productivity" && <Productivity o={o} setO={setO} />}
      {report === "worklist" && <Worklist o={o} setO={setO} />}
      {report === "milestones" && <Milestones o={o} setO={setO} />}
    </div>
  );
}

function useGroups(o: Opts) {
  const { prep } = useProject();
  return useMemo(() => group(prep, { by: o.by, unit: o.unit, filter: o.filter }), [prep, o.by, o.unit, o.filter]);
}

const label = (key: string[]) => key.map((k) => k || "(none)").join(" / ");

// ---------------- Summary ----------------

function Summary({ o, setO }: { o: Opts; setO: (o: Opts) => void }) {
  const { project, periods, nodes } = useProject();
  const [view, setView] = useState<"evm" | "pfe" | "cobra">("evm");
  const { groups, total } = useGroups(o);
  const s = project.settings;
  const mult = o.cost ? s.laborRate : 1;
  const rows = groups.map((g) => ({ g, m: metricsAt(g, o.asOf, s) })).filter(({ m }) => m.bac || m.pv || m.ev || m.ac || m.fv);
  const tm = metricsAt(total, o.asOf, s);
  const titleOf = (key: string[]) => key.map((k, i) => nodes[o.by[i]]?.find((n) => n.code === k)?.title ?? "").filter(Boolean).join(" / ");
  const acAllocated = o.by.every((b) => ["dbs", "wbs", "obs", "pbs"].includes(b));
  const H = (v: number | null) => fmtNum(v === null ? null : v * mult);
  const cols: { h: string; f: (m: Metrics, g: Agg) => ReactNode; x: (m: Metrics, g: Agg) => Cell; cls?: string }[] = [
    { h: o.cost ? "Budget $" : "Budget", f: (m) => H(m.bac), x: (m) => m.bac * mult },
    { h: "Weighting %", f: (m) => fmtPct(m.bac / (tm.bac || NaN)), x: (m) => (tm.bac ? m.bac / tm.bac : null) },
    { h: "Planned", f: (m) => H(m.pv), x: (m) => m.pv * mult },
    { h: "Earned", f: (m) => H(m.ev), x: (m) => m.ev * mult },
    { h: "Actual", f: (m) => H(m.ac), x: (m) => m.ac * mult },
    { h: "Planned %", f: (m) => fmtPct(m.pctPlanned), x: (m) => m.pctPlanned },
    { h: "Earned %", f: (m) => <b>{fmtPct(m.pctEarned)}</b>, x: (m) => m.pctEarned },
    { h: "Forecast %", f: (m) => fmtPct(m.pctForecast), x: (m) => m.pctForecast },
    { h: "Actuals %", f: (m) => fmtPct(m.pctExpended), x: (m) => m.pctExpended },
    { h: "Of project %", f: (m) => fmtPct(m.ev / (tm.bac || NaN), 2), x: (m) => (tm.bac ? m.ev / tm.bac : null) },
    { h: "Earned this period", f: (m) => H(m.evPeriod), x: (m) => m.evPeriod * mult },
    { h: "Actual this period", f: (m) => H(m.acPeriod), x: (m) => m.acPeriod * mult },
    { h: "SPI", f: (m) => fmtIdx(m.spi), x: (m) => m.spi, cls: "bg-slate-50" },
    { h: "CPI", f: (m) => fmtIdx(m.cpi), x: (m) => m.cpi, cls: "bg-slate-50" },
    { h: "CSI", f: (m) => fmtIdx(m.csi), x: (m) => m.csi, cls: "bg-slate-50" },
    { h: "SPI period", f: (m) => fmtIdx(m.spiPeriod), x: (m) => m.spiPeriod },
    { h: "CPI period", f: (m) => fmtIdx(m.cpiPeriod), x: (m) => m.cpiPeriod },
    { h: "SV", f: (m) => H(m.sv), x: (m) => m.sv * mult },
    { h: "CV", f: (m) => H(m.cv), x: (m) => m.cv * mult },
    { h: "ETC", f: (m) => H(m.etc), x: (m) => m.etc * mult },
    { h: "EAC", f: (m) => <b>{H(m.eac1)}</b>, x: (m) => (m.eac1 === null ? null : m.eac1 * mult) },
    { h: "Basic EAC", f: (m) => H(m.eacBasic), x: (m) => m.eacBasic * mult },
    { h: "VAC", f: (m) => <span className={cn((m.vac ?? 0) < 0 && "text-red-600")}>{H(m.vac)}</span>, x: (m) => (m.vac === null ? null : m.vac * mult) },
    { h: "TCPI", f: (m) => fmtIdx(m.tcpi), x: (m) => m.tcpi },
  ];
  const exportEvm = (): OutSheet => {
    const head: Cell[] = [...o.by.map((b) => BD_LABEL[b]), ...(o.by.length ? ["Title"] : []), ...cols.map((c) => c.h)];
    const body = rows.map(({ g, m }) => [...g.key, ...(o.by.length ? [titleOf(g.key)] : []), ...cols.map((c) => c.x(m, g))]);
    const tot = [...o.by.map((_, i) => (i === 0 ? "TOTAL" : "")), ...(o.by.length ? [""] : []), ...cols.map((c) => c.x(tm, total))];
    return { name: "Progress summary", headerRow: 2, rows: [[`${project.code} ${project.name}`, `As of ${periods[o.asOf].cutoff}`], [], head, ...body, tot] };
  };
  // Legacy P/F/E layout: rows = groups, column groups per period with cumulative planned / forecast / earned %.
  const pfe = useMemo(() => {
    const idx = periods.map((_, i) => i);
    return [...groups, total].map((g) => ({ g, series: idx.map((i) => metricsAt(g, i, s)) }));
  }, [groups, total, periods, s]);
  // Cesar's layout: periods across the top, four rows per line item (planned, forecast, earned, actuals), cumulative %.
  const exportPfe = (): OutSheet[] => {
    const name = (g: Agg) => (g === total ? "TOTAL" : label(g.key));
    const rowsOut: Cell[][] = [[o.by.map((b) => BD_LABEL[b]).join(" / ") || "Total", "Budget", "Measure", ...periods.map((p) => xlDate(p.cutoff))]];
    for (const { g, series } of pfe) {
      const measures: [string, (m: Metrics, i: number) => Cell][] = [
        ["Planned %", (m) => m.pctPlanned],
        ["Forecast %", (m) => m.pctForecast],
        ["Earned %", (m, i) => (i <= o.asOf ? m.pctEarned : null)],
        ["Actuals %", (m, i) => (i <= o.asOf ? m.pctExpended : null)],
      ];
      measures.forEach(([h, f], k) => rowsOut.push([k === 0 ? name(g) : "", k === 0 ? series[o.asOf].bac : null, h, ...series.map((m, i) => f(m, i))]));
    }
    // The previous layout (one row per item, P/F/E/A column groups per period), kept as a second sheet.
    const head1: Cell[] = [o.by.map((b) => BD_LABEL[b]).join(" / ") || "Total", "Budget"];
    const head2: Cell[] = ["", ""];
    periods.forEach((p) => {
      head1.push(xlDate(p.cutoff), null, null, null);
      head2.push("P %", "F %", "E %", "A %");
    });
    const body = pfe.map(({ g, series }) => [name(g), series[o.asOf].bac, ...series.flatMap((m, i) => [m.pctPlanned, m.pctForecast, i <= o.asOf ? m.pctEarned : null, i <= o.asOf ? m.pctExpended : null])]);
    return [
      { name: "PFE by period", headerRow: 0, rows: rowsOut },
      { name: "PFE one row per item", headerRow: 1, rows: [head1, head2, ...body] },
    ];
  };
  const viewTabs = <Tabs value={view} onChange={setView} items={[{ value: "evm", label: "EVM table" }, { value: "pfe", label: "P/F/E by period" }, { value: "cobra", label: "EVM STD Format" }]} />;
  if (view === "cobra") return <CobraReport o={o} setO={setO} tabs={viewTabs} />;
  return (
    <>
      <Toolbar o={o} setO={setO} extra={viewTabs} />
      {!acAllocated && <p className="mb-2 text-sm text-amber-700">Actual hours are allocated by DBS/WBS/OBS/PBS only, so grouping by {o.by.filter((b) => !["dbs", "wbs", "obs", "pbs"].includes(b)).map((b) => BD_LABEL[b]).join(", ")} shows their actuals under "(none)".</p>}
      {view === "evm" ? (
        <Card title={o.cost ? "EVM summary ($)" : `EVM summary (${o.unit === "$" ? "$" : "hours"})`} actions={<ExportButton file={`${project.code}-progress-summary.xlsx`} sheets={() => [exportEvm()]} />} bodyClass="p-0">
          <div className="print-fit overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="sticky left-0 bg-slate-50 px-3 py-2 text-left">{o.by.map((b) => BD_LABEL[b]).join(" / ") || "Project"}</th>
                  {cols.map((c) => (
                    <th key={c.h} className={cn("whitespace-nowrap px-2 py-2 text-right", c.cls)}>
                      {c.h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ g, m }) => (
                  <tr key={g.key.join("|")} className="num border-t border-slate-100 text-right hover:bg-slate-50">
                    <td className="sticky left-0 whitespace-nowrap bg-surface px-3 py-1.5 text-left">
                      <b>{label(g.key)}</b> <span className="text-slate-500">{titleOf(g.key)}</span>
                    </td>
                    {cols.map((c) => (
                      <td key={c.h} className={cn("whitespace-nowrap px-2 py-1.5", c.cls)}>
                        {c.f(m, g)}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="num border-t-2 border-slate-300 bg-slate-50 text-right font-semibold">
                  <td className="sticky left-0 bg-slate-50 px-3 py-2 text-left">Total</td>
                  {cols.map((c) => (
                    <td key={c.h} className={cn("whitespace-nowrap px-2 py-2", c.cls)}>
                      {c.f(tm, total)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <p className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
            Each row computes its own indices and EAC from its own sums; totals are never added up from the rows. EAC = (BAC − EV) / CPI + AC. Basic EAC = (BAC − EV) + AC.
          </p>
        </Card>
      ) : (
        <Card title="Cumulative planned / forecast / earned / actuals % by period" actions={<ExportButton file={`${project.code}-pfe.xlsx`} sheets={exportPfe} />} bodyClass="p-0">
          <div className="print-fit overflow-x-auto">
            <table className="text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="sticky left-0 bg-slate-50 px-2 py-1 text-left">{o.by.map((b) => BD_LABEL[b]).join(" / ") || "Project"}</th>
                  <th className="px-2 py-1 text-left" />
                  {periods.map((p, i) => (
                    <th key={p.number} className={cn("whitespace-nowrap border-l border-slate-200 px-2 py-1", i === o.asOf && "bg-brand-50")}>
                      {fmtDate(p.cutoff)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pfe.map(({ g, series }) =>
                  (
                    [
                      ["Planned", "", (m: Metrics) => m.pctPlanned, false],
                      ["Forecast", "text-amber-700", (m: Metrics) => m.pctForecast, false],
                      ["Earned", "text-emerald-700", (m: Metrics) => m.pctEarned, true],
                      ["Actuals", "text-red-700", (m: Metrics) => m.pctExpended, true],
                    ] as const
                  ).map(([h, cls, f, pastOnly], k) => (
                    <tr key={(g === total ? "total" : g.key.join("|")) + h} className={cn("num text-right", k === 0 && "border-t border-slate-200", g === total && "bg-slate-50 font-semibold")}>
                      <td className="sticky left-0 whitespace-nowrap bg-surface px-2 py-0.5 text-left">{k === 0 ? (g === total ? "Total" : label(g.key)) : ""}</td>
                      <td className={cn("whitespace-nowrap px-2 py-0.5 text-left", cls)}>{h}</td>
                      {series.map((m, i) => (
                        <td key={i} className={cn("border-l border-slate-100 px-1.5 py-0.5", cls)}>
                          {pastOnly && i > o.asOf ? "" : fmtPct(f(m), 0)}
                        </td>
                      ))}
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

// ---------------- Curve ----------------

function Curve({ o, setO }: { o: Opts; setO: (o: Opts) => void }) {
  const { project, periods } = useProject();
  const [mode, setMode] = useState<"pct" | "hours">("pct");
  const [monthly, setMonthly] = useState(false);
  const [indirectLayer, setIndirectLayer] = useState(false);
  const [pick, setPick] = useState<string>("__total");
  const { groups, total } = useGroups(o);
  const s = project.settings;
  const g = pick === "__total" ? total : groups.find((x) => x.key.join("|") === pick) ?? total;
  const idx = periods.map((_, i) => i).filter((i) => !monthly || periods[i].monthEnd);
  const ms = idx.map((i) => metricsAt(g, i, s));
  const past = (k: number) => idx[k] <= o.asOf;
  const mult = o.cost ? s.laborRate : 1;
  const val = (m: Metrics, k: "pv" | "fv" | "ev" | "ac") => (mode === "pct" ? m[k] / (m.bac || NaN) : m[k] * mult);
  const series: SeriesDef[] = [
    { key: "p", label: "Planned", color: COLORS.planned, values: ms.map((m) => val(m, "pv")) },
    { key: "f", label: "Forecast", color: COLORS.forecast, values: ms.map((m) => val(m, "fv")), dashed: true },
    { key: "e", label: "Earned", color: COLORS.earned, values: ms.map((m, k) => (past(k) ? val(m, "ev") : null)) },
    { key: "a", label: "Actuals", color: COLORS.actual, values: ms.map((m, k) => (past(k) ? val(m, "ac") : null)) },
  ];
  // Rule 20: indirects as an optional layer (planned flat spread and actuals), kept separate from the EVM curves.
  const showInd = indirectLayer && !s.includeIndirects;
  const ind = indirectBuckets(g, idx.map((i) => [i]), o.asOf);
  if (showInd) {
    const v = (h: number | null) => (h === null ? null : mode === "pct" ? h / (g.bacInd || NaN) : h * mult);
    series.push({ key: "ip", label: "Indirect planned", color: COLORS.indirectPlanned, values: ind.map((d) => v(d.cumPlanned)), dashed: true });
    series.push({ key: "ia", label: "Indirect actuals", color: COLORS.indirect, values: ind.map((d) => v(d.cumActual)) });
  }
  const trend = [
    { key: "spi", label: "SPI", color: COLORS.planned, values: ms.map((m, k) => (past(k) ? m.spi : null)) },
    { key: "cpi", label: "CPI", color: COLORS.actual, values: ms.map((m, k) => (past(k) ? m.cpi : null)) },
  ];
  const labels = idx.map((i) => fmtDate(periods[i].cutoff));
  const marker = idx.findIndex((i) => i >= o.asOf);
  // Cumulative values per period: the S-curve's numbers, shared by the table under the chart and the Excel export.
  const rows = ms.map((m, k) => {
    const p = past(k);
    return {
      period: periods[idx[k]].number,
      cutoff: periods[idx[k]].cutoff,
      current: idx[k] === o.asOf,
      bac: m.bac * mult,
      pv: m.pv * mult,
      fv: m.fv * mult,
      ev: p ? m.ev * mult : null,
      ac: p ? m.ac * mult : null,
      pctPlanned: m.pctPlanned,
      pctForecast: m.pctForecast,
      pctEarned: p ? m.pctEarned : null,
      pctActual: p ? m.pctExpended : null,
      spi: p ? m.spi : null,
      cpi: p ? m.cpi : null,
    };
  });
  const unit = o.cost ? "$" : "h";
  return (
    <>
      <ExperimentNote layout="A" />
      <Toolbar
        o={o}
        setO={setO}
        extra={
          <>
            <Field label="Show">
              <Select className="w-52" value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Curve group">
                <option value="__total">Overall (all selected)</option>
                {groups.map((x) => (
                  <option key={x.key.join("|")} value={x.key.join("|")}>
                    {label(x.key)}
                  </option>
                ))}
              </Select>
            </Field>
            <Tabs value={mode} onChange={setMode} items={[{ value: "pct", label: "%" }, { value: "hours", label: o.cost ? "$" : "Hours" }]} />
            <Checkbox label="Month ends only" checked={monthly} onChange={setMonthly} />
            {!s.includeIndirects && <Checkbox label="Indirect layer" checked={indirectLayer} onChange={setIndirectLayer} />}
          </>
        }
      />
      <Card
        title="S-curve"
        actions={
          <ExportButton
            file={`${project.code}-s-curve.xlsx`}
            sheets={() => [
              {
                name: "S-curve",
                headerRow: 2,
                rows: [
                  [`${project.code} ${project.name}`, pick === "__total" ? "Overall" : label(g.key), `As of ${periods[o.asOf].cutoff}`],
                  [],
                  ["Period", "Cutoff", o.cost ? "Budget $" : "Budget", `Planned${o.cost ? " $" : ""}`, `Forecast${o.cost ? " $" : ""}`, `Earned${o.cost ? " $" : ""}`, `Actuals${o.cost ? " $" : ""}`, "Planned %", "Forecast %", "Earned %", "Actuals %", "SPI", "CPI", ...(showInd ? [`Indirect planned${o.cost ? " $" : ""}`, `Indirect actuals${o.cost ? " $" : ""}`] : [])],
                  ...rows.map((r, k) => [r.period, xlDate(r.cutoff), r.bac, r.pv, r.fv, r.ev, r.ac, r.pctPlanned, r.pctForecast, r.pctEarned, r.pctActual, r.spi, r.cpi, ...(showInd ? [ind[k].cumPlanned * mult, ind[k].cumActual === null ? null : ind[k].cumActual * mult] : [])]),
                ],
              },
            ]}
          />
        }
      >
        <LineChart name={`${project.code}-s-curve`} labels={labels} series={series} height={380} marker={marker} yFormat={mode === "pct" ? (v) => `${(v * 100).toFixed(0)}%` : (v) => fmtNum(v)} />
        <p className="mt-2 text-xs text-slate-500">% = value ÷ budget in effect that period. An approved change order raises the budget, so % curves drop (sawtooth) until milestones catch up.</p>
      </Card>
      <Card className="mt-4" title={`Values by period (cumulative${o.cost ? ", $" : ", hours"})`} bodyClass="p-3">
        <PeriodGrid
          columns={idx.map((i) => gridCol(periods[i], i === o.asOf))}
          rows={[
            { group: `Cumulative (${unit})`, label: "Budget", values: rows.map((r) => r.bac), fmt: fmtGrid.num },
            { group: `Cumulative (${unit})`, label: "Planned", color: COLORS.planned, values: rows.map((r) => r.pv), fmt: fmtGrid.num },
            { group: `Cumulative (${unit})`, label: "Forecast", color: COLORS.forecast, values: rows.map((r) => r.fv), fmt: fmtGrid.num },
            { group: `Cumulative (${unit})`, label: "Earned", color: COLORS.earned, values: rows.map((r) => r.ev), fmt: fmtGrid.num },
            { group: `Cumulative (${unit})`, label: "Actuals", color: COLORS.actual, values: rows.map((r) => r.ac), fmt: fmtGrid.num },
            { group: "Progress %", label: "Planned", color: COLORS.planned, values: rows.map((r) => r.pctPlanned), fmt: fmtGrid.pct },
            { group: "Progress %", label: "Forecast", color: COLORS.forecast, values: rows.map((r) => r.pctForecast), fmt: fmtGrid.pct },
            { group: "Progress %", label: "Earned", color: COLORS.earned, values: rows.map((r) => r.pctEarned), fmt: fmtGrid.pct },
            { group: "Progress %", label: "Actuals", color: COLORS.actual, values: rows.map((r) => r.pctActual), fmt: fmtGrid.pct },
            { group: "Indices", label: "SPI", values: rows.map((r) => r.spi), fmt: fmtGrid.idx },
            { group: "Indices", label: "CPI", values: rows.map((r) => r.cpi), fmt: fmtGrid.idx },
            ...(showInd ? indirectRows(ind, mult, unit) : []),
          ]}
          marker={marker}
        />
        <p className="mt-2 text-xs text-slate-500">Highlighted column: the as-of cutoff. Earned, actuals and indices are blank after it. {monthly ? "Month ends only." : ""}</p>
      </Card>
      <Card className="mt-4">
        <LineChart title="Performance indices (cumulative)" name={`${project.code}-indices`} labels={labels} series={trend} height={260} marker={marker} yFormat={(v) => v.toFixed(2)} />
      </Card>
    </>
  );
}

// ---------------- Shared: period buckets, grid columns, experiment note ----------------

// FIXME(before launch): the progress curve has two layouts while Cesar and Julio compare them (Oct 2026): A is
// "curve" (separate charts, values table below), B is "curve-combined" (one chart on the table's columns). Pick one,
// then delete the other report, its nav item in Layout.tsx, its tour entry and this note.
function ExperimentNote({ layout }: { layout: "A" | "B" }) {
  const { project } = useProject();
  const other = layout === "A" ? "curve-combined" : "curve";
  return (
    <div className="no-print mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
      <FlaskConical className="size-4 shrink-0" />
      <b>Experiment, layout {layout} of 2.</b>
      <span>
        {layout === "A" ? "Separate S-curve and indices charts, values table across the periods." : "S-curve and histogram in one chart, drawn on the same period columns as the table."} We'll keep one
        layout.
      </span>
      <Link to="/p/$projectId/reports/$report" params={{ projectId: project._id, report: other }} className="font-medium underline">
        See layout {layout === "A" ? "B" : "A"}
      </Link>
    </div>
  );
}

/**
 * One bucket per period, or with "Month ends only" the periods up to and including each month-end period (anything
 * after the last month end is a final partial bucket).
 */
function useBuckets(monthly: boolean) {
  const { periods } = useProject();
  return useMemo(() => {
    const out: number[][] = [];
    let cur: number[] = [];
    periods.forEach((p, i) => {
      cur.push(i);
      if (!monthly || p.monthEnd) {
        out.push(cur);
        cur = [];
      }
    });
    if (cur.length) out.push(cur);
    return out;
  }, [periods, monthly]);
}

/** Weekly calendars run to 100+ columns; open the grid reports on month ends when the calendar has them. */
const defaultMonthly = (periods: Period[]) => periods.length > 52 && periods.some((p) => p.monthEnd);

/**
 * Per bucket: metrics at its last period (plan, forecast, budget) and at the as-of cutoff when the bucket contains it
 * ("now": earned, actuals, indices; null for future buckets), plus the bucket's period sums for the bars.
 */
function bucketData(g: Agg, buckets: number[][], asOf: number, s: Settings, n: number, stdHours: (i: number) => number) {
  const ms = Array.from({ length: n }, (_, i) => metricsAt(g, i, s));
  return buckets.map((ix) => {
    const last = ix[ix.length - 1];
    const pastIx = ix.filter((i) => i <= asOf);
    const sum = (k: "pvPeriod" | "fvPeriod" | "evPeriod" | "acPeriod", only = ix) => only.reduce((t, i) => t + ms[i][k], 0);
    return {
      last,
      current: ix.includes(asOf),
      end: ms[last],
      now: pastIx.length ? ms[Math.min(last, asOf)] : null,
      std: ix.reduce((t, i) => t + stdHours(i), 0),
      planned: sum("pvPeriod"),
      forecast: sum("fvPeriod"),
      earned: pastIx.length ? sum("evPeriod", pastIx) : null,
      actual: pastIx.length ? sum("acPeriod", pastIx) : null,
    };
  });
}

/**
 * Rule 20: indirect hours per bucket, outside EVM. Planned is the flat budget spread; actuals stop at the as-of cutoff
 * (null for later buckets). Cumulative values run from the first period.
 */
function indirectBuckets(g: Agg, buckets: number[][], asOf: number) {
  const sum = (arr: Float64Array, ix: number[]) => ix.reduce((t, i) => t + (arr[i] ?? 0), 0);
  const upTo = (last: number) => Array.from({ length: last + 1 }, (_, i) => i);
  return buckets.map((ix) => {
    const last = ix[ix.length - 1];
    const pastIx = ix.filter((i) => i <= asOf);
    return {
      planned: sum(g.pvInd, ix),
      actual: pastIx.length ? sum(g.acInd, pastIx) : null,
      cumPlanned: sum(g.pvInd, upTo(last)),
      cumActual: pastIx.length ? sum(g.acInd, upTo(Math.min(last, asOf))) : null,
    };
  });
}
type IndBucket = ReturnType<typeof indirectBuckets>[number];

/** Table rows for the indirect layer, all under one group: cumulative, plus each bucket's own value in the bars' units when `per` is given. */
function indirectRows(ind: IndBucket[], mult: number, unit: string, per?: { label: string; fmt: (v: number) => string; bar: (h: number | null, k: number) => number | null }): GridRow[] {
  const group = `Indirect (${unit})`;
  return [
    { group, label: "Cum. planned", color: COLORS.indirectPlanned, values: ind.map((d) => d.cumPlanned * mult), fmt: fmtGrid.num },
    { group, label: "Cum. actuals", color: COLORS.indirect, values: ind.map((d) => (d.cumActual === null ? null : d.cumActual * mult)), fmt: fmtGrid.num },
    ...(per
      ? [
          { group, label: `Planned ${per.label}`, color: COLORS.indirectPlanned, values: ind.map((d, k) => per.bar(d.planned, k)), fmt: per.fmt },
          { group, label: `Actual ${per.label}`, color: COLORS.indirect, values: ind.map((d, k) => per.bar(d.actual, k)), fmt: per.fmt },
        ]
      : []),
  ];
}

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const gridCol = (p: Period, current: boolean): GridColumn => ({
  period: `P${p.number}`,
  year: p.cutoff.slice(0, 4),
  dayMonth: `${Number(p.cutoff.slice(8, 10))} ${MON[Number(p.cutoff.slice(5, 7)) - 1]}`,
  current,
});
const fmtGrid = {
  num: (v: number) => fmtNum(v),
  pct: (v: number) => (v * 100).toFixed(1),
  idx: (v: number) => v.toFixed(2),
  hc: (v: number) => v.toFixed(1),
};

/** The grid as shown (periods across), for Excel. */
function gridSheet(name: string, title: Cell[], cutoffs: Period[], rows: GridRow[]): OutSheet {
  return {
    name,
    headerRow: 3,
    rows: [title, [], ["", "Period", ...cutoffs.map((p) => `P${p.number}`)], ["", "Cutoff", ...cutoffs.map((p) => xlDate(p.cutoff))], ...rows.map((r) => [r.group ?? "", r.label, ...r.values])],
  };
}

function GroupPick({ groups, pick, setPick }: { groups: Agg[]; pick: string; setPick: (v: string) => void }) {
  return (
    <Field label="Show">
      <Select className="w-52" value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Curve group">
        <option value="__total">Overall (all selected)</option>
        {groups.map((x) => (
          <option key={x.key.join("|")} value={x.key.join("|")}>
            {label(x.key)}
          </option>
        ))}
      </Select>
    </Field>
  );
}

// ---------------- Curve, layout B: S-curve + histogram on the table's columns ----------------

// Julio's Oct 8 mockup (Janus PPCI): period grid on top, progress curves over per-period bars below, same columns.
function CurveCombined({ o, setO }: { o: Opts; setO: (o: Opts) => void }) {
  const { project, periods } = useProject();
  const [mode, setMode] = useState<"pct" | "hours">("pct");
  const [barMode, setBarMode] = useState<"fte" | "hours">("fte");
  const [monthly, setMonthly] = useState(() => defaultMonthly(periods));
  const [tableFirst, setTableFirst] = useState(true);
  const [kinds, setKinds] = useState({ planned: true, forecast: true, earned: false, actual: true });
  const [indirectLayer, setIndirectLayer] = useState(false);
  const [pick, setPick] = useState("__total");
  const { groups, total } = useGroups(o);
  const s = project.settings;
  const g = pick === "__total" ? total : groups.find((x) => x.key.join("|") === pick) ?? total;
  const buckets = useBuckets(monthly);
  const data = useMemo(() => bucketData(g, buckets, o.asOf, s, periods.length, (i) => periods[i].stdHours), [g, buckets, o.asOf, s, periods]);
  const mult = o.cost ? s.laborRate : 1;
  const unit = o.cost ? "$" : "h";
  const per = monthly ? "month" : "period";
  const cum = (k: "pv" | "fv" | "ev" | "ac") => data.map((d) => {
    const m = k === "pv" || k === "fv" ? d.end : d.now;
    return m ? m[k] * mult : null;
  });
  const pcts = {
    p: data.map((d) => d.end.pctPlanned),
    f: data.map((d) => d.end.pctForecast),
    e: data.map((d) => d.now?.pctEarned ?? null),
    a: data.map((d) => d.now?.pctExpended ?? null),
  };
  const lineVals = mode === "pct" ? pcts : { p: cum("pv"), f: cum("fv"), e: cum("ev"), a: cum("ac") };
  const lines: SeriesDef[] = [
    { key: "p", label: "Planned", color: COLORS.planned, values: lineVals.p },
    { key: "f", label: "Forecast", color: COLORS.forecast, values: lineVals.f, dashed: true },
    { key: "e", label: "Earned", color: COLORS.earned, values: lineVals.e },
    { key: "a", label: "Actuals", color: COLORS.actual, values: lineVals.a },
  ];
  // Bars: the bucket's own hours, or headcount = hours ÷ the bucket's standard hours (rule 19).
  const bar = (v: number | null, std: number) => (v === null ? null : barMode === "hours" ? v * mult : v / (std || NaN));
  const barVals = {
    planned: data.map((d) => bar(d.planned, d.std)),
    forecast: data.map((d) => bar(d.forecast, d.std)),
    earned: data.map((d) => bar(d.earned, d.std)),
    actual: data.map((d) => bar(d.actual, d.std)),
  };
  const bars: SeriesDef[] = (
    [
      ["planned", "Planned", COLORS.planned],
      ["forecast", "Forecast", COLORS.forecast],
      ["earned", "Earned", COLORS.earned],
      ["actual", "Actual", COLORS.actual],
    ] as const
  )
    .filter(([k]) => kinds[k])
    .map(([k, l, color]) => ({ key: `bar-${k}`, label: `${l} ${barMode === "fte" ? "headcount" : `${unit} per ${per}`}`, color, values: barVals[k] }));
  const barGroup = barMode === "fte" ? "Headcount" : `This ${per} (${unit})`;
  const barFmt = barMode === "fte" ? fmtGrid.hc : fmtGrid.num;
  // Rule 20: optional indirect layer (lines, bars and table rows), kept out of the EVM numbers above.
  const showInd = indirectLayer && !s.includeIndirects;
  const ind = indirectBuckets(g, buckets, o.asOf);
  if (showInd) {
    const v = (h: number | null) => (h === null ? null : mode === "pct" ? h / (g.bacInd || NaN) : h * mult);
    lines.push(
      { key: "ip", label: "Indirect planned", color: COLORS.indirectPlanned, values: ind.map((d) => v(d.cumPlanned)), dashed: true },
      { key: "ia", label: "Indirect actuals", color: COLORS.indirect, values: ind.map((d) => v(d.cumActual)) },
    );
    bars.push(
      { key: "bar-ip", label: `Indirect planned ${barMode === "fte" ? "headcount" : `${unit} per ${per}`}`, color: COLORS.indirectPlanned, values: ind.map((d, k) => bar(d.planned, data[k].std)), dashed: true },
      { key: "bar-ia", label: `Indirect actual ${barMode === "fte" ? "headcount" : `${unit} per ${per}`}`, color: COLORS.indirect, values: ind.map((d, k) => bar(d.actual, data[k].std)) },
    );
  }
  const rows: GridRow[] = [
    { label: `Budget (${unit})`, values: data.map((d) => d.end.bac * mult), fmt: fmtGrid.num },
    { group: "Progress %", label: "Planned", color: COLORS.planned, values: pcts.p, fmt: fmtGrid.pct },
    { group: "Progress %", label: "Forecast", color: COLORS.forecast, values: pcts.f, fmt: fmtGrid.pct },
    { group: "Progress %", label: "Earned", color: COLORS.earned, values: pcts.e, fmt: fmtGrid.pct, strong: true },
    { group: "Progress %", label: "Actuals", color: COLORS.actual, values: pcts.a, fmt: fmtGrid.pct },
    { group: `Cumulative (${unit})`, label: "Planned", color: COLORS.planned, values: cum("pv"), fmt: fmtGrid.num },
    { group: `Cumulative (${unit})`, label: "Forecast", color: COLORS.forecast, values: cum("fv"), fmt: fmtGrid.num },
    { group: `Cumulative (${unit})`, label: "Earned", color: COLORS.earned, values: cum("ev"), fmt: fmtGrid.num },
    { group: `Cumulative (${unit})`, label: "Actuals", color: COLORS.actual, values: cum("ac"), fmt: fmtGrid.num },
    { group: barGroup, label: "Planned", color: COLORS.planned, values: barVals.planned, fmt: barFmt },
    { group: barGroup, label: "Forecast", color: COLORS.forecast, values: barVals.forecast, fmt: barFmt },
    { group: barGroup, label: "Earned", color: COLORS.earned, values: barVals.earned, fmt: barFmt },
    { group: barGroup, label: "Actual", color: COLORS.actual, values: barVals.actual, fmt: barFmt },
    { group: "Indices", label: "SPI", values: data.map((d) => d.now?.spi ?? null), fmt: fmtGrid.idx },
    { group: "Indices", label: "CPI", values: data.map((d) => d.now?.cpi ?? null), fmt: fmtGrid.idx },
    ...(showInd ? indirectRows(ind, mult, unit, { label: barMode === "fte" ? "HC" : `/ ${per}`, fmt: barFmt, bar: (h, k) => bar(h, data[k].std) }) : []),
  ];
  const cutoffs = data.map((d) => periods[d.last]);
  return (
    <>
      <ExperimentNote layout="B" />
      <Toolbar
        o={o}
        setO={setO}
        extra={
          <>
            <GroupPick groups={groups} pick={pick} setPick={setPick} />
            <Field label="Curves">
              <Tabs value={mode} onChange={setMode} items={[{ value: "pct", label: "%" }, { value: "hours", label: o.cost ? "$" : "Hours" }]} />
            </Field>
            <Field label="Bars">
              <Tabs value={barMode} onChange={setBarMode} items={[{ value: "fte", label: "Headcount" }, { value: "hours", label: o.cost ? "$" : "Hours" }]} />
            </Field>
            <Field label="Bar series">
              <div className="flex gap-2">
                {(Object.keys(kinds) as (keyof typeof kinds)[]).map((k) => (
                  <Checkbox key={k} label={k[0].toUpperCase() + k.slice(1)} checked={kinds[k]} onChange={(v) => setKinds({ ...kinds, [k]: v })} />
                ))}
              </div>
            </Field>
            <Checkbox label="Month ends only" checked={monthly} onChange={setMonthly} />
            {!s.includeIndirects && <Checkbox label="Indirect layer" checked={indirectLayer} onChange={setIndirectLayer} />}
            <Field label="Table">
              <Tabs value={tableFirst ? "top" : "bottom"} onChange={(v) => setTableFirst(v === "top")} items={[{ value: "top", label: "Above" }, { value: "bottom", label: "Below" }]} />
            </Field>
          </>
        }
      />
      <Card
        title={`S-curve and ${barMode === "fte" ? "headcount" : "hours"} by ${per}${pick === "__total" ? "" : ` · ${label(g.key)}`}`}
        actions={<ExportButton file={`${project.code}-s-curve-combined.xlsx`} sheets={() => [gridSheet("S-curve", [`${project.code} ${project.name}`, pick === "__total" ? "Overall" : label(g.key), `As of ${periods[o.asOf].cutoff}`], cutoffs, rows)]} />}
        bodyClass="p-3"
      >
        <PeriodGrid
          columns={data.map((d) => gridCol(periods[d.last], d.current))}
          rows={rows}
          marker={data.findIndex((d) => d.current)}
          tableFirst={tableFirst}
          landscape
          chart={{
            height: 340,
            lines,
            origin: true,
            bars,
            leftFormat: mode === "pct" ? (v) => `${(v * 100).toFixed(0)}%` : (v) => fmtNum(v),
            leftTitle: mode === "pct" ? "Progress %" : `Cumulative ${unit}`,
            rightFormat: barMode === "fte" ? (v) => v.toFixed(1) : (v) => fmtNum(v),
            rightTitle: barMode === "fte" ? "headcount" : `${unit} per ${per}`,
          }}
        />
        <p className="mt-2 text-xs text-slate-500">
          Each column is a {per}; the curves pass through each cutoff at the column's right edge, and the dashed line is the as-of cutoff (highlighted column). Earned, actuals and indices are as of that
          cutoff and blank after it. Headcount = hours ÷ the {per}'s standard hours. Scroll sideways to move the table and chart together.
        </p>
      </Card>
    </>
  );
}

// ---------------- Productivity ----------------

// Julio's Oct 8 mockup (Janus PRO): productivity = earned ÷ actual hours, i.e. CPI (rule 13; Cesar's PF).
function Productivity({ o, setO }: { o: Opts; setO: (o: Opts) => void }) {
  const { project, periods } = useProject();
  const [monthly, setMonthly] = useState(() => defaultMonthly(periods));
  const [showPeriod, setShowPeriod] = useState(true);
  const [tableFirst, setTableFirst] = useState(true);
  const [pick, setPick] = useState("__total");
  const { groups, total } = useGroups(o);
  const s = project.settings;
  const g = pick === "__total" ? total : groups.find((x) => x.key.join("|") === pick) ?? total;
  const buckets = useBuckets(monthly);
  const data = useMemo(() => bucketData(g, buckets, o.asOf, s, periods.length, (i) => periods[i].stdHours), [g, buckets, o.asOf, s, periods]);
  const mult = o.cost ? s.laborRate : 1;
  const unit = o.cost ? "$" : "h";
  const per = monthly ? "month" : "period";
  const cumPf = data.map((d) => d.now?.cpi ?? null);
  const perPf = data.map((d) => (d.earned !== null && d.actual ? d.earned / d.actual : null));
  const rows: GridRow[] = [
    { label: `Budget (${unit})`, values: data.map((d) => d.end.bac * mult), fmt: fmtGrid.num },
    { group: "Progress", label: "Earned %", color: COLORS.earned, values: data.map((d) => d.now?.pctEarned ?? null), fmt: fmtGrid.pct },
    { group: `Cumulative (${unit})`, label: "Earned", color: COLORS.earned, values: data.map((d) => (d.now ? d.now.ev * mult : null)), fmt: fmtGrid.num },
    { group: `Cumulative (${unit})`, label: "Actuals", color: COLORS.actual, values: data.map((d) => (d.now ? d.now.ac * mult : null)), fmt: fmtGrid.num },
    { group: "Productivity", label: "Cumulative", color: COLORS.planned, values: cumPf, fmt: fmtGrid.idx, strong: true },
    { group: "Productivity", label: `This ${per}`, color: COLORS.forecast, values: perPf, fmt: fmtGrid.idx },
  ];
  const lines: SeriesDef[] = [
    { key: "pf", label: "Productivity (cumulative)", color: COLORS.planned, values: cumPf },
    ...(showPeriod ? [{ key: "pfp", label: `Productivity this ${per}`, color: COLORS.forecast, values: perPf, dashed: true }] : []),
  ];
  const cutoffs = data.map((d) => periods[d.last]);
  return (
    <>
      <Toolbar
        o={o}
        setO={setO}
        extra={
          <>
            <GroupPick groups={groups} pick={pick} setPick={setPick} />
            <Checkbox label="Month ends only" checked={monthly} onChange={setMonthly} />
            <Checkbox label={`Productivity this ${per}`} checked={showPeriod} onChange={setShowPeriod} />
            <Field label="Table">
              <Tabs value={tableFirst ? "top" : "bottom"} onChange={(v) => setTableFirst(v === "top")} items={[{ value: "top", label: "Above" }, { value: "bottom", label: "Below" }]} />
            </Field>
          </>
        }
      />
      <Card
        title={`Productivity by ${per}${pick === "__total" ? "" : ` · ${label(g.key)}`}`}
        actions={<ExportButton file={`${project.code}-productivity.xlsx`} sheets={() => [gridSheet("Productivity", [`${project.code} ${project.name}`, pick === "__total" ? "Overall" : label(g.key), `As of ${periods[o.asOf].cutoff}`], cutoffs, rows)]} />}
        bodyClass="p-3"
      >
        <PeriodGrid
          columns={data.map((d) => gridCol(periods[d.last], d.current))}
          rows={rows}
          marker={data.findIndex((d) => d.current)}
          tableFirst={tableFirst}
          landscape
          chart={{ height: 320, lines, refLines: [1], leftInclude: [1.5], leftFormat: (v) => v.toFixed(1), leftTitle: "Productivity" }}
        />
        <p className="mt-2 text-xs text-slate-500">
          Productivity = earned ÷ actual hours (CPI). Above 1.0, more was earned than spent. Cumulative uses totals to each cutoff; "this {per}" uses only that {per}'s earned and actual hours. Blank after
          the as-of cutoff or where there are no actuals.
        </p>
      </Card>
    </>
  );
}

// ---------------- Histogram ----------------

function Histogram({ o, setO }: { o: Opts; setO: (o: Opts) => void }) {
  const { project, periods } = useProject();
  const [pick, setPick] = useState("__total");
  const [mode, setMode] = useState<"fte" | "hours">("fte");
  const [monthly, setMonthly] = useState(false);
  const [kinds, setKinds] = useState({ planned: true, forecast: true, earned: true, actual: true });
  const [indirectLayer, setIndirectLayer] = useState(false);
  const { groups, total } = useGroups(o);
  const s = project.settings;
  const g = pick === "__total" ? total : groups.find((x) => x.key.join("|") === pick) ?? total;
  const buckets = useBuckets(monthly);
  // Rule 20: optional indirect bars and columns, kept out of the EVM bars.
  const showInd = indirectLayer && !s.includeIndirects;
  const ind = indirectBuckets(g, buckets, o.asOf);
  const rows = useMemo(() => {
    const ms = periods.map((_, i) => metricsAt(g, i, s));
    return buckets.map((ix) => {
      const last = periods[ix[ix.length - 1]];
      const pastIx = ix.filter((i) => i <= o.asOf);
      const sum = (k: "pvPeriod" | "fvPeriod" | "evPeriod" | "acPeriod", only = ix) => only.reduce((t, i) => t + ms[i][k], 0);
      return {
        label: monthly ? `${fmtDate(periods[ix[0]].start)} to ${fmtDate(last.cutoff)}` : `P${last.number} ${fmtDate(last.cutoff)}`,
        cutoff: last.cutoff,
        periods: ix.map((i) => periods[i].number),
        std: ix.reduce((t, i) => t + periods[i].stdHours, 0),
        planned: sum("pvPeriod"),
        forecast: sum("fvPeriod"),
        earned: pastIx.length ? sum("evPeriod", pastIx) : null,
        actual: pastIx.length ? sum("acPeriod", pastIx) : null,
      };
    });
  }, [buckets, periods, g, s, o.asOf, monthly]);
  const show = (h: number | null, std: number) => (h === null ? null : mode === "hours" ? h : h / (std || NaN));
  const series = [
    kinds.planned && { key: "p", label: "Planned", color: COLORS.planned, values: rows.map((r) => show(r.planned, r.std)) },
    kinds.forecast && { key: "f", label: "Forecast", color: COLORS.forecast, values: rows.map((r) => show(r.forecast, r.std)) },
    kinds.earned && { key: "e", label: "Earned", color: COLORS.earned, values: rows.map((r) => show(r.earned, r.std)) },
    kinds.actual && { key: "a", label: "Actual", color: COLORS.actual, values: rows.map((r) => show(r.actual, r.std)) },
    showInd && { key: "ip", label: "Indirect planned", color: COLORS.indirectPlanned, values: ind.map((d, k) => show(d.planned, rows[k].std)) },
    showInd && { key: "ia", label: "Indirect actual", color: COLORS.indirect, values: ind.map((d, k) => show(d.actual, rows[k].std)) },
  ].filter(Boolean) as { key: string; label: string; color: string; values: (number | null)[] }[];
  const exportSheet = (): OutSheet => ({
    name: "Histogram",
    headerRow: 0,
    rows: [
      [monthly ? "Month (periods)" : "Period", "Cutoff", "Std hours", "Planned h", "Forecast h", "Earned h", "Actual h", "Planned headcount", "Forecast headcount", "Earned headcount", "Actual headcount", ...(showInd ? ["Indirect planned h", "Indirect actual h", "Indirect planned headcount", "Indirect actual headcount"] : [])],
      ...rows.map((r, k) => [
        monthly ? r.periods.map((n) => `P${n}`).join(" ") : r.periods[0],
        xlDate(r.cutoff),
        r.std,
        r.planned,
        r.forecast,
        r.earned,
        r.actual,
        r.planned / (r.std || NaN),
        r.forecast / (r.std || NaN),
        r.earned === null ? null : r.earned / (r.std || NaN),
        r.actual === null ? null : r.actual / (r.std || NaN),
        ...(showInd ? [ind[k].planned, ind[k].actual, ind[k].planned / (r.std || NaN), ind[k].actual === null ? null : ind[k].actual / (r.std || NaN)] : []),
      ]),
    ],
  });
  return (
    <>
      <Toolbar
        o={o}
        setO={setO}
        extra={
          <>
            <Field label="Show">
              <Select className="w-52" value={pick} onChange={(e) => setPick(e.target.value)}>
                <option value="__total">Overall</option>
                {groups.map((x) => (
                  <option key={x.key.join("|")} value={x.key.join("|")}>
                    {label(x.key)}
                  </option>
                ))}
              </Select>
            </Field>
            <Tabs value={mode} onChange={setMode} items={[{ value: "fte", label: "Headcount" }, { value: "hours", label: "Hours" }]} />
            <Checkbox label="Month ends only" checked={monthly} onChange={setMonthly} />
            {(Object.keys(kinds) as (keyof typeof kinds)[]).map((k) => (
              <Checkbox key={k} label={k[0].toUpperCase() + k.slice(1)} checked={kinds[k]} onChange={(v) => setKinds({ ...kinds, [k]: v })} />
            ))}
            {!s.includeIndirects && <Checkbox label="Indirect layer" checked={indirectLayer} onChange={setIndirectLayer} />}
          </>
        }
      />
      <Card>
        <BarChart
          title={mode === "hours" ? `Hours per ${monthly ? "month" : "period"}` : `Headcount = ${monthly ? "month" : "period"} hours ÷ ${monthly ? "the month's" : "that period's"} standard hours`}
          name={`${project.code}-histogram`}
          labels={rows.map((r) => fmtDate(r.cutoff))}
          series={series}
          height={400}
          yFormat={(v) => (mode === "hours" ? fmtNum(v) : v.toFixed(1))}
        />
      </Card>
      <Card className="mt-4" title="Values" actions={<ExportButton file={`${project.code}-histogram.xlsx`} sheets={() => [exportSheet()]} />} bodyClass="p-0">
        <div className="print-fit max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-right text-xs text-slate-500">
              <tr>
                <th className="px-3 py-1 text-left">{monthly ? "Month" : "Period"}</th>
                <th className="px-3 py-1">Std h</th>
                <th className="px-3 py-1">Planned h</th>
                <th className="px-3 py-1">Forecast h</th>
                <th className="px-3 py-1">Earned h</th>
                <th className="px-3 py-1">Actual h</th>
                <th className="border-l border-slate-200 px-3 py-1">Planned HC</th>
                <th className="px-3 py-1">Forecast HC</th>
                <th className="px-3 py-1">Earned HC</th>
                <th className="px-3 py-1">Actual HC</th>
                {showInd && (
                  <>
                    <th className="border-l border-slate-200 px-3 py-1">Indirect planned h</th>
                    <th className="px-3 py-1">Indirect actual h</th>
                    <th className="px-3 py-1">Indirect planned HC</th>
                    <th className="px-3 py-1">Indirect actual HC</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="num border-t border-slate-100 text-right">
                  <td className="px-3 py-1 text-left">{r.label}</td>
                  <td className="px-3 py-1">{fmtNum(r.std)}</td>
                  <td className="px-3 py-1">{fmtNum(r.planned, 1)}</td>
                  <td className="px-3 py-1">{fmtNum(r.forecast, 1)}</td>
                  <td className="px-3 py-1">{r.earned === null ? "" : fmtNum(r.earned, 1)}</td>
                  <td className="px-3 py-1">{r.actual === null ? "" : fmtNum(r.actual, 1)}</td>
                  <td className="border-l border-slate-100 px-3 py-1">{fmtNum(r.planned / (r.std || NaN), 2)}</td>
                  <td className="px-3 py-1">{fmtNum(r.forecast / (r.std || NaN), 2)}</td>
                  <td className="px-3 py-1">{r.earned === null ? "" : fmtNum(r.earned / (r.std || NaN), 2)}</td>
                  <td className="px-3 py-1">{r.actual === null ? "" : fmtNum(r.actual / (r.std || NaN), 2)}</td>
                  {showInd && (
                    <>
                      <td className="border-l border-slate-100 px-3 py-1">{fmtNum(ind[i].planned, 1)}</td>
                      <td className="px-3 py-1">{ind[i].actual === null ? "" : fmtNum(ind[i].actual, 1)}</td>
                      <td className="px-3 py-1">{fmtNum(ind[i].planned / (r.std || NaN), 2)}</td>
                      <td className="px-3 py-1">{ind[i].actual === null ? "" : fmtNum(ind[i].actual / (r.std || NaN), 2)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">Standard hours are per person per {monthly ? "month (sum of the month's periods)" : "period"}, set on the Calendar page. A two-week period at 40 h a week is 80 h.</p>
      </Card>
    </>
  );
}

// ---------------- Work items list (PDL) ----------------

function Worklist({ o, setO }: { o: Opts; setO: (o: Opts) => void }) {
  const { b, data, prep, project, chains, activeBreakdowns } = useProject();
  const [show, setShow] = useState<Breakdown[]>(["dbs", "wbs"]);
  const st = useMemo(() => new Map(deliverableStatus(prep, o.asOf).map((x) => [x.id, x])), [prep, o.asOf]);
  const ds = b.deliverables
    .filter((d) => (d.unit ?? "h") === o.unit)
    .filter((d) => Object.entries(o.filter).every(([k, v]) => !v?.length || v.includes(d.codes[k as Breakdown] ?? "")))
    .sort((a, c) => compareKeys(data.codeOrder, show, show.map((k) => a.codes[k] ?? ""), show.map((k) => c.codes[k] ?? "")) || a.code.localeCompare(c.code, undefined, { numeric: true }));
  const totalW = ds.reduce((s, d) => s + (st.get(d._id)?.weight ?? d.weight), 0);
  const maxSteps = Math.max(1, ...ds.map((d) => d.steps.length));
  const exportRows = (): OutSheet => {
    const head: Cell[] = ["DLV REFERENCE", "TITLE", ...show.map((k) => BD_LABEL[k]), "CHAIN", "WEIGHT", "WEIGHTING %", "PLANNED %", "EARNED %", "FORECAST %"];
    for (let n = 1; n <= maxSteps; n++) head.push(`MILESTONE${n}`, `CUM${n}`, `PLANNED${n}`, `FORECAST${n}`, `EARNED${n}`);
    return {
      name: "Work items",
      headerRow: 0,
      rows: [
        head,
        ...ds.map((d) => {
          const s = st.get(d._id);
          const ch = chains.get(d.chainCode);
          const r: Cell[] = [d.code, d.title ?? null, ...show.map((k) => d.codes[k] ?? null), d.chainCode, s?.weight ?? d.weight, totalW ? (s?.weight ?? d.weight) / totalW : null, s?.pctPlanned ?? null, s?.pctEarned ?? null, s?.pctForecast ?? null];
          for (let n = 0; n < maxSteps; n++) {
            const x = d.steps[n];
            r.push(x?.milestone ?? null, ch?.steps[n]?.cum ?? null, xlDate(x?.planned), xlDate(x?.forecast), xlDate(x?.earned));
          }
          return r;
        }),
      ],
    };
  };
  return (
    <>
      <Toolbar
        o={o}
        setO={setO}
        allowGroup={false}
        extra={
          <Field label="Breakdown columns">
            <div className="flex flex-wrap gap-2">
              {activeBreakdowns.map((k) => (
                <Checkbox key={k} label={BD_LABEL[k]} checked={show.includes(k)} onChange={(v) => setShow(v ? [...show, k] : show.filter((x) => x !== k))} />
              ))}
            </div>
          </Field>
        }
      />
      <Card title={`${ds.length} work items · ${fmtNum(totalW)} ${o.unit === "$" ? "$" : "h"}`} actions={<ExportButton file={`${project.code}-work-items.xlsx`} sheets={() => [exportRows()]} />} bodyClass="p-0">
        <div className="print-fit max-h-[70vh] overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-2 py-1.5">DLV reference</th>
                {show.map((k) => (
                  <th key={k} className="px-2 py-1.5">
                    {BD_LABEL[k]}
                  </th>
                ))}
                <th className="px-2 py-1.5 text-right">Weight</th>
                <th className="px-2 py-1.5 text-right">Weighting</th>
                <th className="px-2 py-1.5 text-right">Earned</th>
                <th className="px-2 py-1.5">Chain steps (P / F / E)</th>
              </tr>
            </thead>
            <tbody>
              {ds.map((d) => {
                const s = st.get(d._id);
                const ch = chains.get(d.chainCode);
                return (
                  <tr key={d._id} className="border-t border-slate-100 align-top">
                    <td className="px-2 py-1">
                      <b>{d.code}</b>
                      <div className="text-slate-500">{d.title}</div>
                    </td>
                    {show.map((k) => (
                      <td key={k} className="px-2 py-1">
                        {d.codes[k]}
                      </td>
                    ))}
                    <td className="num px-2 py-1 text-right">{fmtNum(s?.weight ?? d.weight, 1)}</td>
                    <td className="num px-2 py-1 text-right">{fmtPct(totalW ? (s?.weight ?? d.weight) / totalW : null, 2)}</td>
                    <td className="num px-2 py-1 text-right font-medium">{fmtPct(s?.pctEarned)}</td>
                    <td className="px-2 py-1">
                      <div className="flex flex-wrap gap-1">
                        {d.steps.map((x, i) => (
                          <span key={i} className={cn("rounded border px-1 py-0.5", x.earned ? "border-emerald-200 bg-emerald-50" : "border-slate-200")}>
                            <b>{x.milestone}</b> {ch ? `${Math.round(ch.steps[i].cum * 100)}%` : ""}
                            <span className="text-slate-500"> {fmtDate(x.planned) || "–"} / {fmtDate(x.forecast) || "–"} / </span>
                            <span className="text-emerald-700">{fmtDate(x.earned) || "–"}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ---------------- Milestone report ----------------

function Milestones({ o, setO }: { o: Opts; setO: (o: Opts) => void }) {
  const { b, project, periods } = useProject();
  const [onlyLate, setOnlyLate] = useState(false);
  const asOf = periods[o.asOf]?.cutoff ?? "";
  const rows = b.deliverables
    .filter((d) => Object.entries(o.filter).every(([k, v]) => !v?.length || v.includes(d.codes[k as Breakdown] ?? "")))
    .flatMap((d) =>
      d.steps.map((s, i) => {
        const actual = s.earned ?? s.forecast ?? null;
        const variance = s.planned && actual ? toDay(actual) - toDay(s.planned) : null;
        const overdue = !s.earned && !!s.planned && s.planned <= asOf;
        return { d, s, i, actual, variance, overdue };
      }),
    )
    .filter((r) => !onlyLate || (r.variance ?? 0) > 0 || r.overdue)
    .sort((a, c) => (a.s.planned ?? "9").localeCompare(c.s.planned ?? "9"));
  return (
    <>
      <Toolbar o={o} setO={setO} allowGroup={false} extra={<Checkbox label="Late or overdue only" checked={onlyLate} onChange={setOnlyLate} />} />
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        {(() => {
          const all = b.deliverables.flatMap((d) => d.steps);
          const earned = all.filter((s) => s.earned).length;
          const overdue = all.filter((s) => !s.earned && s.planned && s.planned <= asOf).length;
          const late = all.filter((s) => s.earned && s.planned && s.earned > s.planned).length;
          return [
            ["Earned", earned],
            ["Open", all.length - earned],
            ["Overdue (planned date passed)", overdue],
            ["Earned late", late],
          ].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-slate-200 bg-surface p-3">
              <div className="text-xs text-slate-500">{k}</div>
              <div className="num text-xl font-semibold">{v}</div>
            </div>
          ));
        })()}
      </div>
      <Card
        title={`${rows.length} milestones`}
        actions={
          <ExportButton
            file={`${project.code}-milestones.xlsx`}
            sheets={() => [
              {
                name: "Milestones",
                headerRow: 0,
                rows: [["DLV REFERENCE", "TITLE", "STEP", "MILESTONE", "DBS", "PLANNED", "FORECAST", "EARNED", "VARIANCE DAYS", "STATUS"], ...rows.map((r) => [r.d.code, r.d.title ?? null, r.i + 1, r.s.milestone, r.d.codes.dbs ?? null, xlDate(r.s.planned), xlDate(r.s.forecast), xlDate(r.s.earned), r.variance, r.s.earned ? "earned" : r.overdue ? "overdue" : "open"])],
              },
            ]}
          />
        }
        bodyClass="p-0"
      >
        <div className="print-fit max-h-[70vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                <th className="px-3 py-1.5">Deliverable</th>
                <th className="px-3 py-1.5">Title</th>
                <th className="px-3 py-1.5">Milestone</th>
                <th className="px-3 py-1.5">Planned</th>
                <th className="px-3 py-1.5">Forecast</th>
                <th className="px-3 py-1.5">Earned</th>
                <th className="px-3 py-1.5 text-right">Variance (days)</th>
                <th className="px-3 py-1.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 2000).map((r) => (
                <tr key={`${r.d._id}-${r.i}`} className="border-t border-slate-100">
                  <td className="px-3 py-1">{r.d.code}</td>
                  <td className="px-3 py-1 text-slate-600">{r.d.title}</td>
                  <td className="px-3 py-1">
                    {r.i + 1}. {r.s.milestone}
                  </td>
                  <td className="px-3 py-1">{fmtDate(r.s.planned)}</td>
                  <td className="px-3 py-1">{fmtDate(r.s.forecast)}</td>
                  <td className="px-3 py-1 text-emerald-700">{fmtDate(r.s.earned)}</td>
                  <td className={cn("num px-3 py-1 text-right", (r.variance ?? 0) > 0 && "text-red-600")}>{r.variance ?? ""}</td>
                  <td className="px-3 py-1">{r.s.earned ? <Badge tone="green">earned</Badge> : r.overdue ? <Badge tone="red">overdue</Badge> : <Badge>open</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ---------------- Cobra format (Cesar, Oct 20) ----------------

const COBRA_TONE = { plain: "", data: "bg-amber-50", calc: "bg-slate-100", addon: "bg-emerald-50" } as const;

/**
 * The Cobra-style EVM table the big EPCs report from, with the "Cesar Add-On" columns. Rows by one breakdown (DBS by
 * default), optionally in sections with subtotals (discipline vs procurement, or by phase), and an optional indirect
 * section with the total including indirects. Same figures as the EVM table, laid out his way.
 */
function CobraReport({ o, setO, tabs }: { o: Opts; setO: (o: Opts) => void; tabs: ReactNode }) {
  const { project, periods, nodes, prep, activeBreakdowns } = useProject();
  const s = project.settings;
  const rowsBy = o.by[0] ?? "dbs";
  const hasTypes = nodes.dbs.some((n) => n.dbsType === "procurement");
  const [sectionsBy, setSectionsBy] = useState<"none" | "dbsType" | Breakdown>(rowsBy === "dbs" && hasTypes ? "dbsType" : "none");
  const [eac, setEac] = useState<EacBasis>("eac1");
  const [indirectLayer, setIndirectLayer] = useState(false);
  const toast = useToast();
  const mult = o.cost ? s.laborRate : 1;
  const t = useMemo(
    () =>
      buildCobra(prep, {
        rowsBy,
        sectionsBy: sectionsBy === rowsBy ? "none" : sectionsBy,
        filter: o.filter,
        asOf: o.asOf,
        unit: o.unit,
        settings: s,
        eac,
        mult,
        indirectLayer,
        titles: Object.fromEntries(activeBreakdowns.map((b) => [b, new Map(nodes[b].map((n) => [n.code, n.title]))])),
        dbsTypes: new Map(nodes.dbs.map((n) => [n.code, n.dbsType])),
      }),
    [prep, rowsBy, sectionsBy, o.filter, o.asOf, o.unit, s, eac, mult, indirectLayer, activeBreakdowns, nodes],
  );
  const showCraft = !o.cost && o.unit === "h";
  const cols = COBRA_COLUMNS.filter((c) => showCraft || c.fmt !== "hc");
  const fmt = (f: string, v: number | null) => (f === "pct" ? fmtPct(v) : f === "idx" ? fmtIdx(v) : f === "hc" ? (v === null || !isFinite(v) ? "–" : v.toFixed(1)) : fmtNum(v));
  const groups: { name: string; span: number; addon: boolean }[] = [];
  for (const c of cols) {
    const g = groups.at(-1);
    if (g && g.name === c.group) g.span++;
    else groups.push({ name: c.group, span: 1, addon: c.tone === "addon" });
  }
  const firstAddon = cols.findIndex((c) => c.tone === "addon");
  const addonEdge = (i: number) => (i === firstAddon ? "border-l-2 border-l-red-500" : i === cols.length - 1 ? "border-r-2 border-r-red-500" : "");
  const Row = ({ row }: { row: CobraRow }) => {
    const strong = row.kind !== "row" && row.kind !== "indirect";
    return (
      <tr className={cn("border-b border-dashed border-slate-200", strong && "border-t border-solid border-t-slate-400 font-semibold", row.kind === "total" && "border-b-2 border-b-slate-500", row.kind === "grand" && "bg-violet-50")}>
        <td className={cn("sticky left-0 whitespace-nowrap bg-surface px-2 py-1", strong ? "text-right" : "pl-4", row.kind === "indirect" && "text-violet-700")}>{row.label}</td>
        {cols.map((c, i) => {
          const v = row.v[c.key];
          return (
            <td key={c.key} className={cn("num whitespace-nowrap px-2 py-1 text-right", row.kind === "row" || c.tone !== "data" ? COBRA_TONE[c.tone] : "", addonEdge(i), c.key === "vac" && (v ?? 0) < 0 && "text-red-600")}>
              {fmt(c.fmt, v)}
            </td>
          );
        })}
      </tr>
    );
  };
  const exportXlsx = async () => {
    try {
      const buf = await writeCobraWorkbook(t, {
        title: `${project.code} ${project.name}`,
        project: project.code,
        period: `P${periods[o.asOf].number}`,
        cutoff: xlDate(periods[o.asOf].cutoff)!,
        unit: o.cost ? "$" : o.unit,
        eac,
        rowsLabel: rowsBy === "dbs" ? "Discipline (DBS)" : BD_LABEL[rowsBy],
      });
      download(buf, `${project.code}-progress-table-P${periods[o.asOf].number}.xlsx`);
    } catch (e) {
      toast({ text: String((e as Error).message ?? e), tone: "error" });
    }
  };
  return (
    <>
      <Toolbar
        o={o}
        setO={setO}
        allowGroup={false}
        extra={
          <>
            {tabs}
            <Field label="Rows">
              <Select aria-label="Rows" className="w-28" value={rowsBy} onChange={(e) => setO({ ...o, by: [e.target.value as Breakdown] })}>
                {activeBreakdowns.map((b) => (
                  <option key={b} value={b}>
                    {BD_LABEL[b]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Sections">
              <Select aria-label="Sections" className="w-48" value={sectionsBy} onChange={(e) => setSectionsBy(e.target.value as "none")}>
                <option value="none">None</option>
                {rowsBy === "dbs" && <option value="dbsType">Discipline / procurement</option>}
                {activeBreakdowns
                  .filter((b) => b !== rowsBy && nodes[b].length)
                  .map((b) => (
                    <option key={b} value={b}>
                      By {BD_LABEL[b]}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label="TCPI to EAC uses">
              <Tabs value={eac} onChange={setEac} items={[{ value: "eac1", label: "EAC1" }, { value: "basic", label: "Basic EAC" }]} />
            </Field>
            {!s.includeIndirects && <Checkbox label="Indirect layer" checked={indirectLayer} onChange={setIndirectLayer} />}
          </>
        }
      />
      <Card
        title={`Progress table, ${o.cost ? "$" : o.unit === "$" ? "$" : "hours"} · P${periods[o.asOf].number} · ${periods[o.asOf].stdHours} work hours in period`}
        actions={
          <Button onClick={() => void exportXlsx()}>
            <FileDown className="size-4" /> Export
          </Button>
        }
        bodyClass="p-0"
      >
        <div className="print-fit print-zoom overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-600">
              <tr className="text-[10px] italic text-slate-500">
                <th className="sticky left-0 bg-surface" />
                {cols.map((c, i) => (
                  <th key={c.key} className={cn("whitespace-nowrap px-2 pt-1 text-center font-normal", i === firstAddon && "text-red-600")}>
                    {c.key === "tcpiEac" ? (eac === "eac1" ? "(BAC−EV)÷(EAC1−AC)" : "(BAC−EV)÷(Basic−AC)") : c.tag}
                  </th>
                ))}
              </tr>
              <tr className="border-t-2 border-slate-500 bg-slate-50">
                <th className="sticky left-0 bg-slate-50 px-2 py-1 text-left" rowSpan={3}>
                  {rowsBy === "dbs" ? "Discipline (DBS)" : BD_LABEL[rowsBy]}
                </th>
                {groups.map((g, k) => (
                  <th key={k} colSpan={g.span} className={cn("border-l border-slate-200 px-2 py-1 text-center", g.addon && "border-x-2 border-x-red-500 border-t-2 border-t-red-500 bg-emerald-50")}>
                    {g.name}
                  </th>
                ))}
              </tr>
              {[0, 1].map((line) => (
                <tr key={line} className={cn("bg-slate-50", line === 1 && "border-b-2 border-slate-500")}>
                  {cols.map((c, i) => (
                    <th key={c.key} className={cn("whitespace-nowrap px-2 text-center font-medium", c.tone === "addon" && "bg-emerald-50", addonEdge(i))}>
                      {c.key === "tcpiEac" && line === 0 ? (eac === "eac1" ? "Perf. to EAC1" : "Perf. to Basic EAC") : c.head[line]}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {t.sections.map((sec, k) => (
                <SectionRows key={k} label={sec.label} cols={cols.length} firstAddon={firstAddon}>
                  {sec.rows.map((r) => (
                    <Row key={r.code ?? r.label} row={r} />
                  ))}
                  {sec.subtotal && <Row row={sec.subtotal} />}
                </SectionRows>
              ))}
              <Row row={t.total} />
              {t.indirect && (
                <SectionRows label="Indirects" cols={cols.length} firstAddon={firstAddon}>
                  {t.indirect.rows.map((r) => (
                    <Row key={r.label} row={r} />
                  ))}
                  <Row row={t.indirect.grand} />
                </SectionRows>
              )}
            </tbody>
          </table>
        </div>
        <div className="space-y-1 border-t border-slate-100 p-3 text-xs text-slate-500">
          <p>
            Every row, subtotal and total computes its own indices and EAC from its own sums; totals are never added up from row indices. Baseline budget = before change orders; BAC = baseline + approved change
            orders. Craft = hours ÷ the period's {t.stdHours} work hours. TCPI EAC uses {eac === "eac1" ? "EAC1" : "Basic EAC"}: with EAC1 it equals the current CPI, with Basic EAC it equals 1.00. A negative VAC is an
            overrun. <span className="text-red-600">Red frame: Mongoose EVM Add-On.</span>
          </p>
        </div>
      </Card>
    </>
  );
}

function SectionRows({ label, cols, firstAddon, children }: { label: string | null; cols: number; firstAddon: number; children: ReactNode }) {
  return (
    <>
      {label && (
        <tr>
          <td className="sticky left-0 bg-surface px-2 pb-1 pt-3 font-semibold underline">{label}</td>
          {Array.from({ length: cols }, (_, i) => (
            <td key={i} className={cn(i === firstAddon && "border-l-2 border-l-red-500", i === cols - 1 && "border-r-2 border-r-red-500")} />
          ))}
        </tr>
      )}
      {children}
    </>
  );
}
